import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import { logAnalyticsEvent } from "../../Firebase";
import "./Public.css";
import Footer from "../../components/Footer";
import useSeo from "../../hooks/useSeo";

const pubUrl = process.env.PUBLIC_URL || "";

/** Desktop-friendly in-view helper (animate once) */
function useInView(ref, { threshold = 0.2, rootMargin = "0px 0px -10% 0px" } = {}) {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold, rootMargin }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [ref, threshold, rootMargin]);

  return inView;
}

export default function Inspections() {
  useSeo({
  title: "Roof Inspections & Documentation | Crowned Roofing (Toronto, ON)",
  description:
    "Commercial-first roof inspections focused on documentation, condition assessment, leak investigations, and practical recommendations you can act on.",
  ogImage: "https://www.crownedroofing.com/images/ogimg.png",
  canonical: "https://www.crownedroofing.com/inspections",
});


  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Roof Inspections | Crowned Roofing";
    const meta = document.querySelector("meta[name='description']");
    if (meta) {
      meta.setAttribute(
        "content",
        "Commercial roof inspections focused on documented conditions, identified risks, and practical decision-making for repair, maintenance, and capital planning."
      );
    }
  }, []);

  const heroImg = `${pubUrl}/images/inspections/inspections-hero.jpg`;
  const reportPreview = `${pubUrl}/images/report-preview.png`;

  const deliverables = useMemo(
    () => [
      "Detailed roof condition summary",
      "Photo documentation of observed conditions",
      "Identified concerns & priority areas",
      "Context for repair vs. replacement",
      "Referenceable inspection record",
    ],
    []
  );

  const triggers = useMemo(
    () => [
      "Active or recurring leaks",
      "Prior to budgeting or capital planning",
      "After severe weather events",
      "Before committing to major repairs",
      "When documentation is outdated or unclear",
    ],
    []
  );

  // section refs for subtle fades
  const sProblemRef = useRef(null);
  const sProcessRef = useRef(null);
  const sDeliverRef = useRef(null);
  const sReportRef = useRef(null);
  const sWhenRef = useRef(null);
  const sCtaRef = useRef(null);

  const problemIn = useInView(sProblemRef, { threshold: 0.2 });
  const processIn = useInView(sProcessRef, { threshold: 0.2 });
  const deliverIn = useInView(sDeliverRef, { threshold: 0.2 });
  const reportIn = useInView(sReportRef, { threshold: 0.2 });
  const whenIn = useInView(sWhenRef, { threshold: 0.2 });
  const ctaIn = useInView(sCtaRef, { threshold: 0.2 });

  const requestInspection = () => {
    logAnalyticsEvent("inspection_cta_click", { source: "inspections_page" });
    navigate("/contact");
  };

  const viewSample = () => {
    logAnalyticsEvent("sample_report_click", { source: "inspections_page" });
    navigate("/portfolio");
  };

  return (
    <div className="inspPage">
      <Header    />

      {/* HERO */}
      <section className="inspHero">
        <div className="inspHeroBg" style={{ backgroundImage: `url(${heroImg})` }} aria-hidden="true" />
        <div className="inspHeroShade" aria-hidden="true" />

        <div className="inspHeroInner">
          <div className="inspHeroCopy">
            <h1>
              Roof Inspections <br />
              Built for <span>Informed Decisions</span>
            </h1>
            <p>
              Inspection services focused on documenting roof conditions, identifying risks, and supporting repair,
              maintenance, and capital planning decisions.
            </p>

            <button className="inspBtn inspBtnPrimary" onClick={requestInspection}>
              REQUEST AN INSPECTION
            </button>

            <button className="inspLink" onClick={viewSample}>
              View a Sample Report →
            </button>
          </div>
        </div>
      </section>

      {/* PROBLEM / WHY */}
      <section className="inspSection inspSectionLight" ref={sProblemRef}>
        <div className={`inspContainer inspFade ${problemIn ? "in" : ""}`}>
          <div className="inspTwoCol">
            <div>
              <h2 className="inspH2">Most roofing decisions fail before work begins</h2>
              <p className="inspP">
                On commercial properties, roof decisions are often made under pressure—after leaks become disruptive or
                when budgets are already set. Without clear documentation, teams are forced to choose between partial
                repairs, major repairs, or replacement based on assumptions instead of verified conditions.
              </p>

              <div className="inspBullets">
                <div className="inspBullet">Deferred maintenance leads to costly damage</div>
                <div className="inspBullet">Repairs don’t fully resolve the issue</div>
                <div className="inspBullet">Replacements are overscoped or misaligned</div>
                <div className="inspBullet">Budgets are set without current condition data</div>
              </div>
            </div>

            <div className="inspVisualCard" aria-hidden="true">
              <div className="inspVisualIconRow">
                <div className="inspGlyph">📝</div>
                <div className="inspGlyph">📷</div>
                <div className="inspGlyph">📊</div>
                <div className="inspGlyph">✓</div>
              </div>
              <div className="inspVisualText">
                Documentation-first inspections support consistent decisions—internally, with ownership, and across planning cycles.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS + REPORT PREVIEW */}
      <section className="inspSection inspSectionWhite" ref={sProcessRef}>
        <div className={`inspContainer inspFade ${processIn ? "in" : ""}`}>
          <div className="inspTwoCol inspTwoColTight">
            <div>
              <h2 className="inspH2">An Inspection-Led Process</h2>
              <p className="inspP">
                Our inspections are built to create a usable record of roof condition—organized, shareable, and easy to
                reference when planning repairs, maintenance, or capital projects.
              </p>

              <div className="inspCheckList">
                <div className="inspCheckItem">
                  <span className="inspCheckMark" aria-hidden="true">✓</span>
                  Detailed roof condition summary
                </div>
                <div className="inspCheckItem">
                  <span className="inspCheckMark" aria-hidden="true">✓</span>
                  Photo documentation of observed conditions
                </div>
                <div className="inspCheckItem">
                  <span className="inspCheckMark" aria-hidden="true">✓</span>
                  Identified concerns &amp; priority areas
                </div>
                <div className="inspCheckItem">
                  <span className="inspCheckMark" aria-hidden="true">✓</span>
                  Context for repair vs. replacement
                </div>
                <div className="inspCheckItem">
                  <span className="inspCheckMark" aria-hidden="true">✓</span>
                  Referenceable inspection record
                </div>
              </div>
            </div>

            <div className="inspReportPreviewWrap" ref={sReportRef}>
              <div className={`inspReportFrame inspFade ${reportIn ? "in" : ""}`}>
                <img src={reportPreview} alt="Sample roof condition inspection report preview" />
                <button className="inspMiniLink" onClick={viewSample}>
                  View Sample Report →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DELIVERABLES */}
      <section className="inspSection inspSectionLight" ref={sDeliverRef}>
        <div className={`inspContainer inspFade ${deliverIn ? "in" : ""}`}>
          <h2 className="inspH2">What You Receive</h2>
          <p className="inspP">
            Deliverables are structured to support internal review, stakeholder communication, and future planning. We
            focus on observed conditions and documented findings—not assumptions.
          </p>

          <div className="inspDeliverGrid">
            {deliverables.map((d) => (
              <div className="inspDeliverCard" key={d}>
                <div className="inspDeliverMark" aria-hidden="true">✓</div>
                <div className="inspDeliverText">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHEN */}
      <section className="inspSection inspSectionWhite" ref={sWhenRef}>
        <div className={`inspContainer inspFade ${whenIn ? "in" : ""}`}>
          <h2 className="inspH2">When an Inspection Makes Sense</h2>

          <div className="inspWhenGrid">
            {triggers.map((t) => (
              <div className="inspWhenItem" key={t}>
                <span className="inspWhenDot" aria-hidden="true" />
                {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="inspSection inspSectionLight" ref={sCtaRef}>
        <div className={`inspContainer inspFade ${ctaIn ? "in" : ""}`}>
          <div className="inspCtaCard">
            <h2 className="inspH2 inspH2Cta">Start With an Inspection</h2>
            <p className="inspP inspPCta">
              If you need clarity on roof condition before making decisions, an inspection provides the information to move
              forward with confidence.
            </p>

            <button className="inspBtn inspBtnPrimary" onClick={requestInspection}>
              REQUEST AN INSPECTION
            </button>
          </div>
        </div>
      </section>
      <Footer/>
    </div>
  );
}
